import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { byName } from '../lib/catalogModel';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import AdminPage from '../components/AdminShell';
import './AdminEngagePage.css';

// Social Engage (v0.2). Admin clicks Run; the backend looks at schools whose
// first ProZpects athlete arrived in the last 24h (or one typed school) and lists
// accounts to follow. Nothing is posted or followed automatically — people do it here
// by hand. Admin only. Backend: backend/app/routers/admin_engage.py.

export const PLATFORM_LABELS = { x: 'X', instagram: 'Instagram', facebook: 'Facebook' };
// The team role is labelled with the run's sport ("Soccer team"): see roleLabel.
export const ROLE_LABELS = {
  program: 'Athletic program', team: 'Team', ad: 'Athletic director',
  booster: 'Boosters', athlete: 'Athlete',
};
// The open run's sport name (catalog name). Runs from before the sport picker read as Football.
const SportName = createContext('Football');
const roleLabel = (role, sportName) => (role === 'team' ? `${sportName || 'Football'} team` : (ROLE_LABELS[role] || role));
const SCHOOL_STATUS = {
  'skipped:no_website': 'no website found',
  'skipped:junk': 'not a school name',
  waiting: 'waiting for the next run',
  failed: 'failed',
  running: 'stopped mid-way',
};
const POLL_MS = 3000;
// School-account rows sort in this role order (unknown roles last), then by id.
const ROLE_ORDER = ['program', 'team', 'ad', 'booster'];
// Channel columns, left to right; anything else goes in an "Other" column if present.
const CHANNELS = ['x', 'instagram', 'facebook'];
const EVENT_LABELS = { search: 'Search', read: 'Read', note: 'Note', error: 'Error' };
const RUN_STATE = { running: 'Running', done: 'Finished', failed: 'Failed' };

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const safeHref = (u) => (typeof u === 'string' && /^https?:\/\//i.test(u.trim()) ? u.trim() : null);
const detail = (err, fallback) => err?.response?.data?.detail || fallback;

// Server timestamps are UTC without a tz suffix; parse as UTC, show local.
const parseUtc = (iso) => (iso ? new Date(/([zZ])|([+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`) : null);
const formatDateTime = (iso) => {
  const d = parseUtc(iso);
  if (!d) return '';
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, `
    + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};
const formatClock = (iso) => {
  const d = parseUtc(iso);
  return d ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' }) : '';
};
const formatElapsed = (ms) => {
  if (!(ms >= 0)) return '';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = String(total % 60).padStart(2, '0');
  if (h) return `${h}h ${String(m).padStart(2, '0')}m`;
  return m ? `${m}m ${sec}s` : `${total}s`;
};
const formatCost = (usd) => (usd != null ? `~$${usd.toFixed(2)}` : '—');
const hostOf = (href) => {
  try { return new URL(href).host.replace(/^www\./, ''); } catch { return href; }
};
// "School 2 of 5: New Trier — searching" -> { at: 2, of: 5, step: "New Trier — searching" }
const parseProgress = (text) => {
  const m = /^School (\d+) of (\d+):?\s*(.*)$/.exec(text || '');
  return m ? { at: Number(m[1]), of: Number(m[2]), step: m[3] } : { at: null, of: null, step: text || '' };
};

function ExternalLink({ href, children, className = 'adm-link', ...rest }) {
  return <a className={className} href={href} target="_blank" rel="noopener noreferrer" {...rest}>{children}</a>;
}

function Handle({ url, handle }) {
  const href = safeHref(url);
  return href
    ? <ExternalLink className="adm-link eng-row__handle" href={href}>@{handle}</ExternalLink>
    : <span className="eng-row__handle">@{handle}</span>;
}

function PlatformBadge({ platform }) {
  return (
    <span className={`eng-platform eng-platform--${platform}`}>{PLATFORM_LABELS[platform] || platform}</span>
  );
}

// Inside a channel column the column names the platform, so the badge is shown only
// where channels mix (the Other column). School rows carry their role label now that
// they are no longer under role subheadings.
function ActionRow({ action, onStatus, showPlatform }) {
  const evidenceHref = safeHref(action.evidence_url);
  const open = action.status === 'open';
  const athlete = action.role === 'athlete';
  const sportName = useContext(SportName);
  const role = athlete ? null : roleLabel(action.role, sportName);
  return (
    <li className={`eng-row${open ? '' : ' eng-row--closed'}${showPlatform ? ' eng-row--badged' : ''}`}>
      {showPlatform && <PlatformBadge platform={action.platform} />}
      <div className="eng-row__main">
        <div className="eng-row__line">
          <Handle url={action.url} handle={action.handle} />
          {action.confidence === 'likely' && <span className="adm-chip adm-chip--amber">please confirm</span>}
        </div>
        {action.profile_summary && <p className="eng-row__summary">{action.profile_summary}</p>}
        {role || (athlete && action.label) || evidenceHref ? (
          <div className="eng-row__sub">
            {role && <span className="eng-row__role">{role}</span>}
            {athlete && action.label && <span>{action.label}</span>}
            {evidenceHref && (
              <ExternalLink className="eng-row__evidence" href={evidenceHref}
                title={action.evidence_text || ''} aria-label={`Evidence for @${action.handle}`}>
                evidence: {hostOf(evidenceHref)}
              </ExternalLink>
            )}
          </div>
        ) : null}
      </div>
      <div className="eng-row__buttons">
        {open ? (
          <>
            <button type="button" className="adm-btn adm-btn--sm eng-btn-done" aria-label={`Mark @${action.handle} done`}
              onClick={() => onStatus(action.id, 'done')}>Done</button>
            <button type="button" className="adm-btn adm-btn--sm" aria-label={`Skip @${action.handle}`}
              onClick={() => onStatus(action.id, 'skipped')}>Skip</button>
          </>
        ) : (
          <>
            <span className={`adm-chip ${action.status === 'done' ? 'adm-chip--green' : ''}`}>{action.status}</span>
            <button type="button" className="adm-link eng-row__undo" aria-label={`Reopen @${action.handle}`}
              onClick={() => onStatus(action.id, 'open')}>Undo</button>
          </>
        )}
      </div>
    </li>
  );
}

const roleRank = (role) => {
  const i = ROLE_ORDER.indexOf(role);
  return i === -1 ? ROLE_ORDER.length : i;
};
const byRoleThenId = (a, b) => (roleRank(a.role) - roleRank(b.role)) || (a.id - b.id);

function Section({ title, rows, onStatus, showPlatform }) {
  return (
    <section className="eng-section" aria-label={title}>
      <h4 className="eng-section__title">{title}</h4>
      <ul className="eng-rows">
        {rows.map((a) => <ActionRow key={a.id} action={a} onStatus={onStatus} showPlatform={showPlatform} />)}
      </ul>
    </section>
  );
}

// One column per channel: an empty slot for the welcome post (filled in phase 1), then
// school accounts, then athletes. Counts follow the open-only filter.
function ChannelColumn({ channel, label, all, openOnly, onStatus }) {
  const shown = openOnly ? all.filter((a) => a.status === 'open') : all;
  const school = shown.filter((a) => a.role !== 'athlete').sort(byRoleThenId);
  const athletes = shown.filter((a) => a.role === 'athlete').sort((a, b) => a.id - b.id);
  const showPlatform = channel === 'other';
  let empty = null;
  if (!all.length) empty = `No accounts on ${label}`;
  else if (!shown.length) empty = `Nothing open on ${label}.`;
  return (
    <section className={`eng-channel eng-channel--${channel}`} aria-label={label}>
      <h3 className="eng-channel__title">
        {label}<span className="eng-channel__count" aria-hidden="true">{shown.length}</span>
      </h3>
      <div className="eng-channel__post" />
      {school.length > 0 && (
        <Section title="School accounts" rows={school} onStatus={onStatus} showPlatform={showPlatform} />
      )}
      {athletes.length > 0 && (
        <Section title="Athletes" rows={athletes} onStatus={onStatus} showPlatform={showPlatform} />
      )}
      {empty && <p className="eng-none">{empty}</p>}
    </section>
  );
}

function RejectedList({ school }) {
  const [open, setOpen] = useState(false);
  const sportName = useContext(SportName);
  const items = school.rejected || [];
  if (!items.length) return null;
  const listId = `eng-rejected-${school.id}`;
  return (
    <div className="eng-rejected">
      <button type="button" className="eng-rejected__toggle" aria-expanded={open} aria-controls={listId}
        onClick={() => setOpen((v) => !v)}>
        Rejected by check ({items.length})
      </button>
      {open && (
        <ul className="eng-rejected__list" id={listId}>
          {items.map((x) => {
            const evidenceHref = safeHref(x.evidence_url);
            return (
              <li key={x.id} className="eng-rejected__item">
                <div className="eng-row__line">
                  <PlatformBadge platform={x.platform} />
                  <Handle url={x.url} handle={x.handle} />
                  <span className="eng-rejected__role">{roleLabel(x.role, sportName)}</span>
                </div>
                {x.profile_summary && <p className="eng-row__summary">{x.profile_summary}</p>}
                <p className="eng-rejected__reason">{x.reason}</p>
                {evidenceHref && (
                  <ExternalLink className="eng-row__evidence" href={evidenceHref}
                    title={x.evidence_text || ''} aria-label={`Evidence for @${x.handle}`}>
                    evidence: {hostOf(evidenceHref)}
                  </ExternalLink>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function SchoolCard({ school, openOnly, onStatus }) {
  const columns = CHANNELS.map((c) => ({
    channel: c, label: PLATFORM_LABELS[c], all: school.actions.filter((x) => x.platform === c),
  }));
  const other = school.actions.filter((x) => !CHANNELS.includes(x.platform));
  if (other.length) columns.push({ channel: 'other', label: 'Other', all: other });
  const website = safeHref(school.website);
  const athletics = safeHref(school.athletics_site);
  const titleId = `eng-school-${school.id}`;
  return (
    <section className="adm__card eng-school" aria-labelledby={titleId}>
      <header className="eng-school__head">
        <div className="eng-school__title">
          <h2 className="eng-school__name" id={titleId}>{school.name}</h2>
          <div className="eng-school__place">{school.town ? `${school.town}, ${school.state}` : school.state}</div>
        </div>
        {school.trigger_name && (
          <span className="adm-chip adm-chip--green eng-school__new">New athlete: {school.trigger_name}</span>
        )}
      </header>
      {(website || athletics || school.mascot) && (
        <dl className="eng-facts">
          {website && (
            <div className="eng-facts__item">
              <dt>Website</dt>
              <dd>
                <ExternalLink href={website}>{hostOf(website)}</ExternalLink>
                {school.website_source === 'found' && <span className="adm-chip eng-facts__chip">found by search</span>}
              </dd>
            </div>
          )}
          {athletics && athletics !== website && (
            <div className="eng-facts__item">
              <dt>Athletics</dt>
              <dd><ExternalLink href={athletics}>{hostOf(athletics)}</ExternalLink></dd>
            </div>
          )}
          {school.mascot && (
            <div className="eng-facts__item"><dt>Mascot</dt><dd>{school.mascot}</dd></div>
          )}
        </dl>
      )}

      <div className={`eng-channels${columns.length > 3 ? ' eng-channels--four' : ''}`}>
        {columns.map((c) => (
          <ChannelColumn key={c.channel} channel={c.channel} label={c.label} all={c.all}
            openOnly={openOnly} onStatus={onStatus} />
        ))}
      </div>
      <RejectedList school={school} />
    </section>
  );
}

const STICK_PX = 40;
const shortSchool = (name) => (name || '').replace(/\s+high school$/i, '');

// Follows new events only while the reader is at (or within STICK_PX of) the bottom,
// so scrolling up to read is never undone by the next poll. Keyed by run: a new run
// starts at the bottom.
function ActivityFeed({ events, schools }) {
  const ref = useRef(null);
  const stick = useRef(true);
  const last = events.length ? events[events.length - 1].id : null;
  useEffect(() => {
    const el = ref.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [last]);
  if (!events.length) return null;
  const onScroll = (e) => {
    const el = e.currentTarget;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight <= STICK_PX;
  };
  // Name the school on each line only when the run covers more than one.
  const names = schools.length > 1 ? new Map(schools.map((s) => [s.id, shortSchool(s.name)])) : null;
  return (
    <ol className="eng-feed" ref={ref} onScroll={onScroll} role="log" aria-label="Activity" aria-live="polite">
      {events.map((e) => {
        const school = names && e.school_id != null ? names.get(e.school_id) : null;
        return (
          <li key={e.id} className={`eng-feed__item eng-feed__item--${e.kind}`}>
            <span className="eng-feed__time">{formatClock(e.at)}</span>
            <span className="eng-feed__kind">{EVENT_LABELS[e.kind] || e.kind}</span>
            <span className="eng-feed__text">
              {school && <span className="eng-feed__school">{school}</span>}
              {e.text}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function StatusCard({ run, now }) {
  const running = run.status === 'running';
  const p = parseProgress(run.progress);
  const at = running ? p.at : run.schools_processed;
  const of = running ? p.of : run.schools_found;
  const started = parseUtc(run.started_at);
  const ended = running ? now : parseUtc(run.finished_at);
  const elapsed = started && ended ? formatElapsed(ended - started) : '';
  const pct = of ? Math.min(100, Math.round(((running ? Math.max(0, at - 1) : at) / of) * 100)) : 0;
  return (
    <section className={`adm__card eng-status eng-status--${run.status}`} aria-label="Run status">
      <div className="eng-status__head">
        <span className={`eng-state eng-state--${run.status}`}>{RUN_STATE[run.status] || run.status}</span>
        {run.manual_school && <span className="eng-status__target">{run.manual_school}, {run.manual_state}</span>}
        {!run.manual_school && <span className="eng-status__target">Last 24 hours</span>}
      </div>
      {running ? (
        <p className="eng-status__line">{p.step || 'Starting…'}</p>
      ) : (
        <p className="eng-status__line">
          {run.status === 'failed' ? 'Run failed. ' : 'Finished. '}
          {run.schools_processed} of {run.schools_found} schools, {run.actions_created} new actions.
        </p>
      )}
      {of ? (
        <div className="eng-progress" role="progressbar" aria-label="Schools done" aria-valuemin={0}
          aria-valuemax={of} aria-valuenow={running ? Math.max(0, at - 1) : at}>
          <span style={{ width: `${pct}%` }} />
        </div>
      ) : null}
      <dl className="eng-stats">
        {running && at && of ? (
          <div className="eng-stats__item"><dt>Progress</dt><dd>School {at} of {of}</dd></div>
        ) : null}
        {elapsed && <div className="eng-stats__item"><dt>Elapsed</dt><dd>{elapsed}</dd></div>}
        <div className="eng-stats__item"><dt>Cost</dt><dd>{formatCost(run.est_cost_usd)}</dd></div>
        <div className="eng-stats__item"><dt>Sport</dt><dd>{run.sport_name || 'Football'}</dd></div>
        <div className="eng-stats__item"><dt>Class first</dt><dd>{run.grade_name || 'Any class'}</dd></div>
        {run.model_label && <div className="eng-stats__item"><dt>Model</dt><dd>{run.model_label}</dd></div>}
      </dl>
      <ActivityFeed key={run.id} events={run.events || []} schools={run.schools || []} />
    </section>
  );
}

export default function AdminEngagePage() {
  const { API_URL } = useAuth();
  const base = `${API_URL}/admin/engage`;
  const [runs, setRuns] = useState([]);
  const [run, setRun] = useState(null);
  const [models, setModels] = useState([]);
  const [model, setModel] = useState('');
  const [sportList, setSportList] = useState([]);
  const [sport, setSport] = useState('');
  const [classList, setClassList] = useState([]);
  const [grade, setGrade] = useState('');
  const [school, setSchool] = useState('');
  const [state, setState] = useState('');
  const [openOnly, setOpenOnly] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const [now, setNow] = useState(() => new Date());

  const wantedRun = useRef(null);
  const loadRun = useCallback((id) => {
    wantedRun.current = id;
    return axios.get(`${base}/runs/${id}`, auth()).then((res) => {
      if (res.data.id !== wantedRun.current) return;
      setRun(res.data);
    });
  }, [base]);
  const loadRuns = useCallback(
    () => axios.get(`${base}/runs`, { ...auth(), params: { limit: 10 } }).then((res) => {
      const items = res.data.items || [];
      setRuns(items);
      return items;
    }),
    [base],
  );

  useEffect(() => {
    loadRuns()
      .then((items) => (items.length ? loadRun(items[0].id) : null))
      .catch((err) => setError(detail(err, 'Failed to load runs.')));
  }, [loadRuns, loadRun]);

  // A failed model list leaves the select empty; Run then omits `model` and the server
  // uses its own default.
  useEffect(() => {
    axios.get(`${base}/models`, auth()).then((res) => {
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      setModels(items);
      setModel(items.some((m) => m.id === res.data.default) ? res.data.default : (items[0]?.id || ''));
    }).catch(() => {});
  }, [base]);

  // Sports come from the sport catalog. A failed list leaves the select empty; Run then omits
  // `sport` and the server uses football.
  useEffect(() => {
    axios.get(`${base}/sports`, auth()).then((res) => {
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      setSportList(items);
      setSport(items.some((x) => x.slug === res.data.default) ? res.data.default : (items[0]?.slug || ''));
    }).catch(() => {});
  }, [base]);

  // The class whose athletes come first (older/other classes fill). A failed list leaves the
  // select empty; Run then omits `grade` and the server uses sophomores.
  useEffect(() => {
    axios.get(`${base}/classes`, auth()).then((res) => {
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      setClassList(items);
      setGrade(items.some((x) => x.id === res.data.default) ? res.data.default : (items[0]?.id || ''));
    }).catch(() => {});
  }, [base]);

  const running = run?.status === 'running';
  const runId = run?.id;
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => { loadRun(runId).catch(() => {}); }, POLL_MS);
    return () => clearInterval(t);
  }, [running, runId, loadRun]);

  // Elapsed time ticks once a second while the run is live.
  useEffect(() => {
    if (!running) return undefined;
    setNow(new Date());
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, [running]);

  // When the run we are watching leaves `running`, refresh Recent runs once so its
  // status there catches up.
  const lastSeen = useRef(null);
  const runStatus = run?.status;
  useEffect(() => {
    const prev = lastSeen.current;
    lastSeen.current = runId == null ? null : { id: runId, status: runStatus };
    if (prev && prev.id === runId && prev.status === 'running' && runStatus !== 'running') {
      loadRuns().catch(() => {});
    }
  }, [runId, runStatus, loadRuns]);

  const start = (body) => {
    setStarting(true);
    setError('');
    const choice = { ...body, ...(model ? { model } : {}), ...(sport ? { sport } : {}),
      ...(grade ? { grade } : {}) };
    axios.post(`${base}/runs`, choice, auth())
      .then(
        (res) => loadRun(res.data.run_id).then(() => loadRuns()).catch(() => setError(
          'Run started, but the page could not refresh. Reload to see it.')),
        (err) => setError(detail(err, 'Could not start a run.')),
      )
      .finally(() => setStarting(false));
  };

  const onManual = (e) => {
    e.preventDefault();
    if (!school.trim() || !state.trim()) {
      setError('Enter a school name and a state.');
      return;
    }
    start({ school: school.trim(), state: state.trim() });
  };

  const setStatus = (actionId, status) => axios.patch(`${base}/actions/${actionId}`, { status }, auth())
    .then(() => setRun((prev) => ({
      ...prev,
      schools: prev.schools.map((s) => ({
        ...s, actions: s.actions.map((a) => (a.id === actionId ? { ...a, status } : a)),
      })),
    })))
    .catch((err) => setError(detail(err, 'Could not update the action.')));

  const processed = (run?.schools || []).filter((s) => s.status === 'processed');
  // While the run is live its current school is `running`; that is progress, not a stop.
  const others = (run?.schools || []).filter(
    (s) => s.status !== 'processed' && !(running && s.status === 'running'),
  );
  const nothingNew = run?.status === 'done' && !run.manual_school && !processed.length && !others.length;
  const busy = running || starting;

  return (
    <AdminPage active="engage" title="Social Engage"
      subtitle="Accounts to follow at schools that just got their first ProZpects athlete. Nothing is posted automatically.">
      <section className="adm__card eng-controls" aria-label="Run controls">
        <div className="eng-controls__row">
          <label className="adm-field eng-controls__model">
            <span className="adm-field__label">Model</span>
            <select className="adm-select" value={model} disabled={busy || !models.length}
              onChange={(e) => setModel(e.target.value)}>
              {models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </label>
          <label className="adm-field eng-controls__model">
            <span className="adm-field__label">Sport</span>
            <select className="adm-select" value={sport} disabled={busy || !sportList.length}
              onChange={(e) => setSport(e.target.value)}>
              {[...sportList].sort(byName).map((x) => <option key={x.slug} value={x.slug}>{x.name}</option>)}
            </select>
          </label>
          <label className="adm-field eng-controls__model">
            <span className="adm-field__label">Class first</span>
            <select className="adm-select" value={grade} disabled={busy || !classList.length}
              onChange={(e) => setGrade(e.target.value)}>
              {classList.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </label>
          <button type="button" className="adm-btn adm-btn--primary eng-controls__run" disabled={busy}
            onClick={() => start({})}>
            Run last 24 hours
          </button>
          <label className="eng-openonly">
            <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
            Open only
          </label>
        </div>
        <form className="eng-manual" onSubmit={onManual}>
          <p className="eng-manual__title">Or run one school</p>
          <div className="eng-manual__fields">
            <input className="adm-input eng-manual__school" aria-label="School name" placeholder="School name"
              value={school} onChange={(e) => setSchool(e.target.value)} />
            <input className="adm-input eng-manual__state" aria-label="State" placeholder="State" value={state}
              maxLength={20} onChange={(e) => setState(e.target.value)} />
            <button type="submit" className="adm-btn adm-btn--dark" disabled={busy}>Run one school</button>
          </div>
        </form>
      </section>

      {error && <div className="adm-error">{error}</div>}

      {run && <StatusCard run={run} now={now} />}

      <SportName.Provider value={run?.sport_name || 'Football'}>
        {processed.map((s) => <SchoolCard key={s.id} school={s} openOnly={openOnly} onStatus={setStatus} />)}
      </SportName.Provider>

      {nothingNew && (
        <div className="adm__card eng-card"><div className="adm-empty">No new schools in the last 24 hours.</div></div>
      )}

      {others.length > 0 && (
        <section className="adm__card eng-card" aria-labelledby="eng-others">
          <h2 className="eng-card__title" id="eng-others">Other schools</h2>
          <ul className="eng-others">
            {others.map((s) => (
              <li key={s.id}>{s.name}, {s.state}: {SCHOOL_STATUS[s.status] || s.status}</li>
            ))}
          </ul>
        </section>
      )}

      {run?.errors?.length > 0 && (
        <details className="adm__card eng-card eng-errors">
          <summary>{run.errors.length} problem{run.errors.length === 1 ? '' : 's'} during this run</summary>
          <ul>{run.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </details>
      )}

      {runs.length > 1 && (
        <section className="adm__card adm__card--flush eng-card eng-runs">
          <h2 className="eng-card__title eng-runs__title" id="eng-runs">Recent runs</h2>
          <table className="adm-table eng-runs__table" aria-labelledby="eng-runs">
            <thead>
              <tr><th>When</th><th>Run</th><th>Sport</th><th>Class</th><th>Model</th><th>Status</th><th className="adm-th--right">Cost</th></tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className={r.id === runId ? 'eng-runs__row--current' : undefined}>
                  <td data-label="When">
                    <button type="button" className="adm-link" aria-current={r.id === runId ? 'true' : undefined}
                      onClick={() => loadRun(r.id).catch(() => {})}>
                      {formatDateTime(r.started_at)}
                    </button>
                  </td>
                  <td data-label="Run">{r.manual_school ? `${r.manual_school}, ${r.manual_state}` : 'last 24 hours'}</td>
                  <td data-label="Sport">{r.sport_name || 'Football'}</td>
                  <td data-label="Class">{r.grade_name || 'Any class'}</td>
                  <td data-label="Model">{r.model_label || r.model || '—'}</td>
                  <td data-label="Status">
                    <span className={`adm-chip ${r.status === 'failed' ? 'adm-chip--red' : ''}${r.status === 'running' ? 'adm-chip--amber' : ''}`}>
                      {r.status}
                    </span>
                  </td>
                  <td data-label="Cost" className="adm-td--num">{r.est_cost_usd != null ? `$${r.est_cost_usd.toFixed(2)}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </AdminPage>
  );
}
